import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, Briefcase, Users, Clock, Shield, Award, MessageCircle, Mail } from 'lucide-react';

const CorporateCarRental = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    { icon: Briefcase, title: 'Business Meetings', description: 'Professional transport for client meetings and conferences' },
    { icon: Users, title: 'Employee Transport', description: 'Reliable daily commute solutions for your team' },
    { icon: Clock, title: 'Airport Transfers', description: 'Timely pickup and drop for business travelers' },
    { icon: Shield, title: 'Event Transportation', description: 'Group transport for corporate events and seminars' },
  ];

  const benefits = [
    'Dedicated account manager for your company',
    'Flexible billing options - monthly or per trip',
    'Priority booking and 24/7 customer support',
    'Professional, uniformed chauffeurs',
    'Well-maintained, premium fleet',
    'GPS tracking and real-time updates',
    'Customized packages based on your needs',
    'Transparent pricing with detailed invoicing',
  ];

  const handleBookNow = () => {
    // Show inquiry options
    const message = 'Hi, I would like to inquire about Corporate Car Rental services from Advait Tour and Travels.';
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleEmailInquiry = () => {
    window.location.href = 'mailto:Advaittourtravel666@gmail.com?subject=Corporate Car Rental Inquiry';
  };

  const handleCallInquiry = () => {
    window.location.href = 'tel:+917998666660';
  };

  return (
    <>
      <SEO
        title="Corporate Car Rental - Business Transportation Solutions | Advait Tour and Travels"
        description="Professional corporate car rental services for businesses. Employee transport, client meetings, airport transfers, and event transportation with dedicated account management."
        keywords="corporate car rental, business cab service, employee transportation, corporate taxi service chandigarh, business travel solutions"
        canonicalUrl={`${window.location?.origin}/corporate-car-rental`}
        ogTitle="Corporate Car Rental - Business Transportation Solutions | Advait Tour and Travels"
        ogDescription="Professional corporate car rental services for businesses. Employee transport, client meetings, airport transfers, and event transportation."
        ogUrl={`${window.location?.origin}/corporate-car-rental`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Corporate Car Rental",
          "description": "Professional transportation solutions for businesses",
          "provider": {
            "@type": "Organization",
            "name": "Advait Tour and Travels"
          }
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Corporate Car Rental
              </h1>
              <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
                Professional Transportation Solutions for Your Business Needs
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={handleBookNow}
                  className="px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-lg rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Inquiry
                </button>
                <button
                  onClick={handleEmailInquiry}
                  className="px-8 py-4 bg-white hover:bg-gray-100 text-blue-600 font-bold text-lg rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  <Mail className="w-5 h-5" />
                  Email Inquiry
                </button>
                <button
                  onClick={handleCallInquiry}
                  className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold text-lg rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </button>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="container mx-auto px-4 mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Corporate Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services?.map((service, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service?.title}</h3>
                  <p className="text-gray-600">{service?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Elevate Your Business Travel Experience</h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  At <strong>Advait Tour and Travels</strong>, we understand that corporate transportation is more than just getting from point A to point B. It's about making the right impression, ensuring punctuality, and providing comfort to your team and clients. Our Corporate Car Rental service is designed to meet the unique needs of businesses, offering reliable, professional, and flexible transportation solutions.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why Choose Our Corporate Car Rental?</h3>
                
                <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {benefits?.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Award className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Perfect For</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-orange-50 rounded-lg p-6">
                    <h4 className="text-xl font-bold text-orange-900 mb-3">Daily Operations</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Employee pickup and drop</li>
                      <li>• Client meeting transportation</li>
                      <li>• Inter-office transfers</li>
                      <li>• Document delivery services</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="text-xl font-bold text-blue-900 mb-3">Special Events</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Corporate conferences</li>
                      <li>• Team building events</li>
                      <li>• Business seminars</li>
                      <li>• Product launches</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Our Fleet</h3>
                <p>
                  We maintain a diverse fleet of vehicles to suit different corporate needs - from executive sedans for individual travel to spacious SUVs and luxury vans for group transportation. All vehicles are regularly serviced, sanitized, and equipped with modern amenities to ensure a comfortable journey.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Flexible Billing Options</h3>
                <p>
                  We offer multiple billing options to suit your company's requirements:
                </p>
                <ul className="space-y-2 ml-6">
                  <li><strong>Monthly Contracts:</strong> Fixed monthly packages with unlimited or capped usage</li>
                  <li><strong>Per Trip Billing:</strong> Pay only for the trips you make</li>
                  <li><strong>Hourly Packages:</strong> Flexible hourly rental for meetings and events</li>
                  <li><strong>Customized Plans:</strong> Tailored packages based on your specific needs</li>
                </ul>

                <div className="bg-green-50 rounded-lg p-6 mt-8">
                  <h3 className="text-xl font-bold text-green-900 mb-3">Get Started Today</h3>
                  <p className="text-gray-700">
                    Ready to streamline your corporate transportation? Contact us for a customized quote and discover how our Corporate Car Rental service can benefit your business. Our team will work with you to understand your requirements and create a solution that fits your budget and schedule.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CorporateCarRental;